import { AxesEnabledDisplay } from 'components/node_settings/AxesEnabledDisplay';
import { SettingEnabledLozenge } from 'components/node_settings/SettingEnabledLozenge';
import { BorderlessSettingsSection } from 'components/node_settings/styled';
import {
    UpdateInterval,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/useV4SensorSettings';
import {
    anyVibrationAxisEnabled,
    getEnabledAxes,
} from 'pages/Settings/V4SensorSettings/helpers';
import { Setting } from 'pages/Settings/V4SensorSettings/Setting';
import {
    SectionDescription,
    SectionHeading,
    SectionTitle,
    SettingsList,
} from 'pages/Settings/V4SensorSettings/styled';
import { ReactNode } from 'react';
import { Lozenge } from 'shared/lozenge/Lozenge';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const ValueRow = styled.div`
    display: flex;
    gap: 0.4rem;
    align-items: center;
`;

const Placeholder = styled.span`
    color: ${tokens.text.secondary};
`;

function ModeLozenge({ children }: { children: ReactNode }) {
    return (
        <Lozenge
            backgroundColor={tokens.lozenge.grey4Background}
            color={tokens.text.onColor}
        >
            {children}
        </Lozenge>
    );
}

function UpdateIntervalValue({ updateInterval }: { updateInterval: UpdateInterval }) {
    switch (updateInterval.mode) {
        case 'smartsample':
            return <span>SMARTsample</span>;
        case 'triggered':
            return (
                <ValueRow>
                    <span>{updateInterval.thresholdG}g</span>
                    <ModeLozenge>TRIGGERED</ModeLozenge>
                </ValueRow>
            );
        case 'timed':
            return (
                <ValueRow>
                    <span>{updateInterval.intervalMinutes} minutes</span>
                    <ModeLozenge>TIMED</ModeLozenge>
                </ValueRow>
            );
        default:
            updateInterval satisfies never;
            return null;
    }
}

type Props = Readonly<{
    settings: V4SensorSettings;
}>;

export function CollectionSettingsSection({ settings }: Props) {
    const allAxesDisabled = !anyVibrationAxisEnabled(settings);
    const { lowFrequencyCollection, updateInterval } = settings;

    return (
        <BorderlessSettingsSection>
            <SectionHeading>
                <SectionTitle>Collection Settings</SectionTitle>
                <SectionDescription>
                    These are the basic settings that define data collection for V4
                    sensors at this location.
                </SectionDescription>
            </SectionHeading>
            <SettingsList>
                <Setting name="Axes Enabled">
                    <AxesEnabledDisplay enabledAxes={getEnabledAxes(settings)} />
                </Setting>
                <Setting name="Sampling Frequency">
                    {allAxesDisabled ? (
                        <Placeholder>
                            Enable Vibration Data for X, Y, or Z axes to sample data.
                        </Placeholder>
                    ) : (
                        `${settings.samplingFrequencyHz} Hz`
                    )}
                </Setting>
                <Setting name="Number of Samples">
                    {allAxesDisabled ? (
                        <Placeholder>
                            Enable Vibration Data for X, Y, or Z axes to sample data.
                        </Placeholder>
                    ) : (
                        `${settings.numberOfSamples} Samples`
                    )}
                </Setting>
                <Setting
                    name="Low-Frequency Collection (Y Axis)"
                    helperText="Enables a separate, lower-frequency collection. Useful for detecting slow-speed faults without impacting the primary collection. Must be less than or equal to the primary collection interval."
                >
                    <SettingEnabledLozenge enabled={lowFrequencyCollection.enabled} />
                </Setting>
                {lowFrequencyCollection.enabled && (
                    <>
                        <Setting name="Sampling Frequency" indented>
                            {lowFrequencyCollection.samplingFrequencyHz} Hz
                        </Setting>
                        <Setting name="Number of Samples" indented>
                            {lowFrequencyCollection.numberOfSamples} Samples
                        </Setting>
                    </>
                )}
                <Setting name="Update Interval">
                    <UpdateIntervalValue updateInterval={updateInterval} />
                </Setting>
                {updateInterval.mode === 'triggered' && (
                    <>
                        <Setting name="Collection Offset (Forward)" indented>
                            {updateInterval.collectionOffsetMs} ms
                        </Setting>
                        <Setting name="Minimum Time Between Triggers" indented>
                            {updateInterval.minTimeBetweenTriggersMinutes} minutes
                        </Setting>
                    </>
                )}
            </SettingsList>
        </BorderlessSettingsSection>
    );
}
